/**
 * Assessment Domain — Barrel Export
 *
 * Public API for the assessment domain.
 * DDD co-locatie s59: types, utils, and hooks consolidated here.
 *
 * Architecture:
 * - types.ts: All assessment type definitions
 * - hooks/useAssessmentSession: Session state management
 * - utils/: Blueprint generation, eligibility, storage, recovery, reports
 * - components/: UI (AssessmentPage, AssessmentReportPage, etc.)
 */

// ── Types ────────────────────────────────────────────────────
export type {
  AssessmentConfig,
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentAnswer,
  AssessmentReport,
  AssessmentHistoryEntry,
  BlueprintQuestion,
  SelectionPlan,
  ModulePerformance,
  TierPerformance,
  FamilyPerformance,
  PracticeRecommendation,
  ModuleLevelEstimate,
  GrowthIndicator,
  SkillGroupedArea,
  ActionPlan,
  AssessmentDomainId,
  SubTestDefinition,
  TeacherAssignment,
  CooldownResult,
} from './types'

// ── Session Hook ─────────────────────────────────────────────
export { useAssessmentSession } from './hooks/useAssessmentSession'

// ── Utils (re-export key functions) ──────────────────────────
export {
  getAssessmentHistory,
  checkAssessmentCooldown,
  checkDomainCooldown,
  getReport,
  getReportsByGroup,
} from './utils/assessmentStorage'
export {
  getRecoverableSession,
  clearSessionState,
} from './utils/assessmentSessionRecovery'
export type { RecoverableSession } from './utils/assessmentSessionRecovery'

// ── Domain Config ────────────────────────────────────────────
export {
  DOMAIN_META,
  DOMAIN_MODULE_MAP,
  getSubTestDefinition,
  getAvailableSubTests,
  isBegrijpendLezenFamily,
  isTaalverzorgingTaaFamily,
} from './utils/assessmentDomainConfig'

// ── Blueprint & Eligibility ──────────────────────────────────
export { generateAssessmentBlueprint } from './utils/assessmentBlueprintGenerator'
export { checkAssessmentEligibility } from './utils/assessmentEligibility'
export { buildAssessmentReport } from './utils/assessmentReportBuilder'

// ── UI Components ────────────────────────────────────────────
// NOTE: AssessmentIntroScreen and AssessmentCompleteScreen are internal to
// AssessmentSession and intentionally NOT exported from the barrel.
export { AssessmentPage } from './components/AssessmentPage'
export { AssessmentSession } from './components/AssessmentSession'
export { AssessmentReportPage } from './components/AssessmentReportPage'
export { AssessmentRecoveryModal } from './components/AssessmentRecoveryModal'
export { AssessmentErrorModal } from './components/AssessmentErrorModal'
export { AssignedAssessmentCompleteScreen } from './components/AssignedAssessmentCompleteScreen'
export { BreathingExercise } from './components/BreathingExercise'
